import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { VIDEO_COLUMNS, VideoHead, VideoTr } from '../components/VideosTable';
import { LoadMore, Rows, SearchBox, Segmented, Table } from '../components/data';
import { PageHeader } from '../components/ui';
import { useDebounced } from '../lib/useDebounced';
import { fetchVideos, qk, type VideoStatus } from '../queries/admin';

type StatusFilter = '' | VideoStatus | 'deleted';

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: '', label: 'All' },
  { value: 'ready', label: 'Ready' },
  { value: 'processing', label: 'Processing' },
  { value: 'uploading', label: 'Uploading' },
  { value: 'failed', label: 'Failed' },
  { value: 'deleted', label: 'Deleted' },
];

/** `?status=failed` from the overview lands on the right filter. */
function initialStatus(): StatusFilter {
  const value = new URLSearchParams(window.location.search).get('status');
  return STATUS_OPTIONS.some((option) => option.value === value) ? (value as StatusFilter) : '';
}

export function RecordingsPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilter>(initialStatus);
  const query = useDebounced(q.trim());
  const deleted = status === 'deleted';
  const apiStatus = deleted ? '' : status;

  const videos = useInfiniteQuery({
    queryKey: qk.videos(query, apiStatus, deleted),
    queryFn: ({ pageParam }) => fetchVideos(query, apiStatus, deleted, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  const rows = videos.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PageHeader title="Recordings" subtitle="Every recording on the platform, newest first." />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchBox
          value={q}
          onChange={setQ}
          placeholder="Search by title, share id or owner email"
          busy={videos.isFetching && !videos.isFetchingNextPage}
        />
        <Segmented value={status} onChange={setStatus} options={STATUS_OPTIONS} />
      </div>

      <Table>
        <VideoHead />
        <Rows query={videos} columns={VIDEO_COLUMNS} empty={query || status ? 'No recordings match.' : 'Nothing recorded yet.'}>
          {rows.map((video) => (
            <VideoTr key={video.id} video={video} />
          ))}
        </Rows>
      </Table>
      <LoadMore query={videos} />
    </>
  );
}
